"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import ImageWrapper from "../ImageWrapper";
import { getDefaultDownPayment, calculateEMI, formatEmiCurrency, EMI_DEFAULT_INTEREST_RATE, EMI_DEFAULT_TENURE, EMI_MIN_INTEREST_RATE, EMI_MAX_INTEREST_RATE } from "@/lib/utils/emi";
import { findSpecValue } from "@/app/utilities/modelTagConverter";
import { PDP_EMICALCULATOR_POPUP_TRACKING } from "@/lib/constants/tracking";
import Image from "next/image";
import { formatPrice } from "@/app/utilities/helper";
import BodyContent from "../BodyContent/BodyContent";

// ─── Types ────────────────────────────────────────────────────────────────────
interface SpecEntry {
    key: string;
    label: string;
    value: string | number | boolean;
    valueType?: string;
    valueNormalized?: number;
    unit?: string;
    sortOrder: number;
    _id: string;
}

interface SpecSection {
    key: string;
    label: string;
    order: number;
    specs: SpecEntry[];
    _id: string;
}

interface Variant {
    _id: string;
    name: string;
    slug: string;
    price: number;
    fuel_type: string;
    transmission: string;
    images: string[];
    featured_image: string;
    specSections: SpecSection[];
    is_electric: boolean;
    is_active: boolean;
    is_base_variant: boolean;
    is_top_variant: boolean;
    fuel_types: string[];
    transmissions: string[];
    seating_capacity: number;
    brand: {
        _id: string;
        name: string;
        slug: string;
    };
    model: {
        _id: string;
        name: string;
        slug: string;
        detailed_slug: string;
        featured_image: string;
    };
}

interface EmiCalculatorProps {
    carName?: string;
    variants?: Variant[];
    /** Map of variant _id → on-road price (ex-showroom + RTO + insurance). Falls back to variant.price if not provided. */
    onRoadPrices?: Record<string, number>;
    cityName?: string;
    isMobile?: boolean;
    selectedVariantId?: string;
    onVariantChange?: (variant: Variant) => void;
    isOpen: boolean;
    hideHeader?: boolean;
    hideVariantSelector?: boolean;
    isEmiRoute?: boolean;
    onClose?: () => void;
    VarentSectorClass?: string;
    DownPaymentClass?: string;
    LoanTermClass?: string;
    InterestRateClass?: string;
    mobileRevampLayout?: boolean;
    onMetricsChange?: (metrics: {
        emi: number;
        loanMonths: number;
        effectivePrice: number;
        interestRate: number;
        downPayment: number;
        loanAmount: number;
    }) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const LOAN_PERIODS = [12, 24, 36, 48, 60, 72, 84];
const MIN_INTEREST = EMI_MIN_INTEREST_RATE;
const MAX_INTEREST = EMI_MAX_INTEREST_RATE;

const SCROLLBAR_CLASS =
    "[&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-thumb]:bg-origin-padding [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent";

const SLIDER_CLASS =
    "appearance-none cursor-pointer bg-[linear-gradient(to_right,_#008c8b_0%,_#008c8b_var(--pct,20%),_#d1d5db_var(--pct,20%),_#d1d5db_100%)] rounded-[2px] outline-none w-full h-1 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#008c8b] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:w-[18px] [&::-webkit-slider-thumb]:h-[18px] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:duration-150 [&::-moz-range-thumb]:bg-[#008c8b] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:w-[18px] [&::-moz-range-thumb]:h-[18px]";

const BADGE_CLASS =
    "text-[#111827] cursor-pointer text-center whitespace-nowrap bg-[#f6f6f6] rounded-md min-w-[70px] px-2.5 py-1 text-base font-bold transition-[border-color,box-shadow] duration-150 inline-block hover:border-[#008c8b] hover:shadow-[0_0_0_2px_#0d94881f]";

const BADGE_INPUT_CLASS =
    "text-[#111827] text-center bg-white border-[1.5px] border-[#008c8b] rounded-md outline-none max-w-[80px] px-2 py-1 text-base font-bold shadow-[0_0_0_3px_#0d948826]";


function getVariantMeta(variant: Variant) {

    const engine = findSpecValue(variant, "engine_displacement") ? parseInt(findSpecValue(variant, "engine_displacement")) : 0;
    const fuel = variant.fuel_type ? variant.fuel_types?.[0] : '';
    const transmission = findSpecValue(variant, "transmission_type") ? findSpecValue(variant, "transmission_type") : '';

    return [engine, fuel, transmission].filter(Boolean).join(" • ");
}

// ─── Editable Badge ───────────────────────────────────────────────────────────
function EditableBadge({
    value,
    prefix = "",
    suffix = "",
    min,
    max,
    onCommit,
    onError,
    className,
}: {
    value: number;
    prefix?: string;
    suffix?: string;
    min: number;
    max: number;
    onCommit: (v: number) => void;
    onError?: () => void;
    className?: string;
}) {
    const [editing, setEditing] = useState(false);
    const [raw, setRaw] = useState("");

    const startEdit = () => {
        setRaw(String(value));
        setEditing(true);
    };

    const commit = () => {
        const parsed = parseFloat(raw.replace(/,/g, ""));
        if (!isNaN(parsed)) {
            if (parsed < min || parsed > max) {
                onError?.();
            }
            onCommit(Math.min(max, Math.max(min, parsed)));
        }
        setEditing(false);
    };

    const handleKey = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
    };

    if (editing) {
        return (
            <input
                className={`${BADGE_INPUT_CLASS} ${className ?? ""}`}
                value={`${raw}`}
                onChange={(e) => setRaw(e.target.value)}
                onBlur={commit}
                onKeyDown={handleKey}
                onFocus={(e) => e.currentTarget.select()}
                type="text"
                inputMode="decimal"
                autoFocus
            />
        );
    }

    return (
        <span
            className={`${BADGE_CLASS} ${className ?? ""}`}
            onClick={startEdit}
            title="Click to edit"
        >
            {prefix}
            {suffix === "%" ? value.toFixed(1) : formatEmiCurrency(value)}
            {suffix}
        </span>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmiCalculator({
    carName,
    variants = [],
    onRoadPrices,
    cityName,
    isMobile = false,
    selectedVariantId,
    onVariantChange,
    isOpen,
    hideHeader = false,
    hideVariantSelector = false,
    isEmiRoute = false,
    onClose,
    VarentSectorClass,
    DownPaymentClass,
    LoanTermClass,
    InterestRateClass,
    mobileRevampLayout = false,
    onMetricsChange,

}: EmiCalculatorProps) {

    const hasVariants = variants.length > 0;
    const isModal = typeof onClose === "function";
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
    const [standalonePrice, setStandalonePrice] = useState(() => {
        const firstOnRoadPrice = onRoadPrices ? Object.values(onRoadPrices)[0] : undefined;
        return Math.round(firstOnRoadPrice ?? 2000000);
    });

    const variant = hasVariants ? variants[selectedVariantIdx] : null;
    const selectedVariantMeta = variant ? getVariantMeta(variant) : "";
    const effectivePrice = hasVariants
        ? ((onRoadPrices && variant?._id && onRoadPrices[variant._id]) ? onRoadPrices[variant._id] : (variant?.price ?? 0))
        : standalonePrice;

    // Lazy-init from the initially selected variant's price so the popup
    // never flashes a 0%-down (i.e. too-high) EMI before this settles.
    const [downPayment, setDownPayment] = useState(() => getDefaultDownPayment(effectivePrice));
    const [interestRate, setInterestRate] = useState(EMI_DEFAULT_INTEREST_RATE);
    const [loanMonths, setLoanMonths] = useState(EMI_DEFAULT_TENURE);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [downPaymentError, setDownPaymentError] = useState<string | null>(null);
    const [interestRateError, setInterestRateError] = useState<string | null>(null);
    const [loanMonthsError, setLoanMonthsError] = useState<string | null>(null);
    const [loanAmountError, setLoanAmountError] = useState<string | null>(null);
    const downPaymentErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const interestRateErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loanMonthsErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loanAmountErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const maxDown = effectivePrice;
    const principal = Math.round(effectivePrice - downPayment);
    const { emi, totalPayable } = calculateEMI({ price: effectivePrice, downPayment, interestRate, tenureMonths: loanMonths });
    const totalInterest = Math.round(totalPayable - principal);
    const downPaymentPct = effectivePrice > 0 ? (downPayment / effectivePrice) * 100 : 0;
    const principalPct = totalPayable > 0 ? (principal / totalPayable) * 100 : 0;

    const downPct = maxDown > 0 ? (downPayment / maxDown) * 100 : 0;
    const interestPct =
        ((interestRate - MIN_INTEREST) / (MAX_INTEREST - MIN_INTEREST)) * 100;

    const showFieldError = (
        setter: React.Dispatch<React.SetStateAction<string | null>>,
        timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
        message: string,
    ) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setter(message);
        timerRef.current = setTimeout(() => setter(null), 4000);
    };

    const clampDown = useCallback(
        (v: number) => {
            setDownPayment(Math.min(maxDown, Math.max(0, Math.round(v))));
        },
        [maxDown],
    );

    const clampInterest = (v: number) => {
        setInterestRate(Math.min(MAX_INTEREST, Math.max(MIN_INTEREST, parseFloat(v.toFixed(1)))));
    };

    const clampLoanMonths = (v: number) => {
        setLoanMonths(Math.round(Math.min(84, Math.max(12, v))));
    };

    const handleVariantSelect = (idx: number) => {
        if (!variants[idx]) return;
        setSelectedVariantIdx(idx);
        const variantPrice = (onRoadPrices && variants[idx]?._id && onRoadPrices[variants[idx]._id])
            ? onRoadPrices[variants[idx]._id]
            : variants[idx].price;
        setDownPayment(getDefaultDownPayment(variantPrice));
        setDropdownOpen(false);
        onVariantChange?.(variants[idx]);
    };

    useEffect(() => {
        if (!selectedVariantId) return;

        const nextIndex = variants.findIndex((item) => item._id === selectedVariantId);
        if (nextIndex >= 0) {
            setSelectedVariantIdx(nextIndex);
        }
    }, [selectedVariantId, variants]);

    // Reset downpayment to 10% whenever the effective price changes (variant selection or on-road price load)
    useEffect(() => {
        if (effectivePrice > 0 && hasVariants) {
            setDownPayment(getDefaultDownPayment(effectivePrice));
        }
    }, [effectivePrice]);

    useEffect(() => {
        if (!isModal) return;

        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isModal, isOpen]);

    useEffect(() => {
        if (hasVariants) return;

        setDownPayment((current) => Math.min(current, standalonePrice));
    }, [hasVariants, standalonePrice]);

    useEffect(() => {
        onMetricsChange?.({
            emi,
            loanMonths,
            effectivePrice,
            interestRate,
            downPayment,
            loanAmount: principal,
        });
    }, [emi, loanMonths, effectivePrice, interestRate, downPayment, principal, onMetricsChange]);

    useEffect(() => {
        return () => {
            if (downPaymentErrorTimer.current) clearTimeout(downPaymentErrorTimer.current);
            if (interestRateErrorTimer.current) clearTimeout(interestRateErrorTimer.current);
            if (loanMonthsErrorTimer.current) clearTimeout(loanMonthsErrorTimer.current);
            if (loanAmountErrorTimer.current) clearTimeout(loanAmountErrorTimer.current);
        };
    }, []);

    if (!isOpen) return null;

    const content = (
        <div
            className={
                mobileRevampLayout
                    ? "bg-white p-0"
                    : "bg-white border border-[#e5e7eb] rounded-[14px] p-[18px] max-[639px]:rounded-none max-[639px]:pt-4 max-[639px]:px-4 max-[639px]:pb-7"
            }
        >
            {/* ── Header ── */}
            {!hideHeader && (
                <div className="flex justify-between items-center mb-4 max-[767px]:mb-2">
                    <h2 className="text-[#111827] m-0 text-base font-bold">{carName ? `Calculate EMI Of ${carName}` : "Calculate EMI"}</h2>
                    {isModal && (
                        <button
                            className="text-[#6b7280] cursor-pointer border-none rounded-full shrink-0 flex items-center justify-center w-[30px] h-[30px] transition-colors duration-150 hover:text-[#111] hover:bg-[#e5e7eb]"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                                alt="Previous"
                                title="Previous"
                                width={16}
                                height={16}
                            />
                        </button>
                    )}
                </div>
            )}

            {/* ── Variant Dropdown ── */}
            {!hideVariantSelector && hasVariants && variant && (
                <div className="mb-3.5 relative">
                    <button
                        className={`text-[#155dfc] cursor-pointer bg-[#f3f7ff] border border-[#d2e0ff] rounded-lg flex justify-between items-center w-full px-3.5 py-2.5 text-sm font-semibold ${VarentSectorClass}`}
                        onClick={() => setDropdownOpen((v) => !v)}
                    >
                        <div className="text-left flex-1 flex justify-between items-center gap-3 min-w-0">
                            <div className="flex flex-col gap-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                    <span className="text-[#155dfc] whitespace-nowrap text-ellipsis text-sm font-bold overflow-hidden">{variant.name}</span>
                                    {variant.is_top_variant && (
                                        <span className="rounded-full shrink-0 px-2 py-0.5 text-[10px] font-bold leading-[1.2] text-[#1d4ed8] bg-[#eff6ff]">Top</span>
                                    )}
                                    {variant.is_base_variant && (
                                        <span className="rounded-full shrink-0 px-2 py-0.5 text-[10px] font-bold leading-[1.2] text-[#4b5563] bg-[#f3f4f6]">Base</span>
                                    )}
                                </div>
                                {selectedVariantMeta && (
                                    <span className="text-[#6b7280] text-xs leading-[1.4]">{selectedVariantMeta}</span>
                                )}
                            </div>
                            <span className="text-[#111827] whitespace-nowrap text-[13px] font-bold"><span className="text-[#6b7280] mr-1 text-[11px] font-medium block">Ex-showroom</span> {formatPrice(variant.price)} </span>
                        </div>
                        <ImageWrapper
                            src="/drop-arrow.svg"
                            alt="Dropdown Icon"
                            width={10}
                            height={10}
                            cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
                            className={`text-[#155dfc] [filter:invert(32%)_sepia(98%)_saturate(2486%)_hue-rotate(206deg)_brightness(96%)_contrast(101%)] ${dropdownOpen ? "rotate-180" : ""} ml-2 transition-transform duration-200 ${isOpen ? "" : "rotate-180"
                                }`}
                        />
                    </button>
                    {dropdownOpen && (
                        <ul className="z-20 bg-white border border-[#e5e7eb] rounded-lg max-h-[225px] max-[767px]:max-h-[210px] m-0 py-1 list-none animate-[dropIn_0.14s] absolute top-[calc(100%+4px)] left-0 right-0 overflow-y-auto shadow-[0_8px_24px_#0000001a]">
                            {variants.map((v, i) => (
                                <li
                                    key={v._id || v.name}
                                    className={`text-[#374151] cursor-pointer px-3.5 py-2.5 transition-colors duration-100 hover:text-[#155dfc] hover:bg-[#f3f7ff] ${i === selectedVariantIdx ? "text-[#155dfc] bg-[#f3f7ff] font-semibold" : ""
                                        }`}
                                    onClick={() => handleVariantSelect(i)}
                                >
                                    <div className="flex justify-between items-center gap-3">
                                        <div className="flex flex-col gap-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                                <span className="text-[#111827] whitespace-nowrap text-ellipsis text-sm font-semibold overflow-hidden">{v.name}</span>
                                                {v.is_top_variant && (
                                                    <span className="rounded-full shrink-0 px-2 py-0.5 text-[10px] font-bold leading-[1.2] text-[#1d4ed8] bg-[#eff6ff]">Top</span>
                                                )}
                                                {v.is_base_variant && (
                                                    <span className="rounded-full shrink-0 px-2 py-0.5 text-[10px] font-bold leading-[1.2] text-[#4b5563] bg-[#f3f4f6]">Base</span>
                                                )}
                                            </div>
                                            {getVariantMeta(v) && (
                                                <span className="text-[#6b7280] text-xs leading-[1.4]">{getVariantMeta(v)}</span>
                                            )}
                                        </div>
                                        {!isMobile && (
                                            <span className="text-[#4b5563] whitespace-nowrap text-right text-xs font-bold"><span className="text-[#6b7280] mr-1 text-[11px] font-medium block">Ex-showroom</span>₹{formatEmiCurrency(v.price)}</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* ── On-Road Price Row ── */}
            {!isEmiRoute && (!hasVariants || (onRoadPrices && variant?._id && onRoadPrices[variant._id])) && (
                <div className="flex justify-between items-center mb-3.5 pt-2.5 pb-3 max-[767px]:p-0">
                    <span className="text-[#6b7280] text-sm font-normal max-[767px]:font-medium">
                        On-Road Price in <span className="font-semibold">{cityName}</span>
                    </span>
                    <span className="text-[#111827] text-[15px] font-bold">₹{formatEmiCurrency(effectivePrice)}</span>
                </div>
            )}

            <div>
                {/* ── Sliders Panel (2 col desktop, stacked mobile) ── */}

                <div className="grid grid-cols-2 gap-x-5 gap-y-0 max-[639px]:grid-cols-1 max-[639px]:gap-x-0 max-[639px]:gap-y-3.5">
                    {/* Loan Amount (only when there are no variants) */}
                    {!hasVariants && (
                        <div className="flex flex-col last:pl-1">
                            <div className="flex justify-between items-center mb-2.5">
                                <span className="text-[#374151] text-[13px] font-medium max-[767px]:text-[11px] max-[767px]:font-normal">Loan Amount</span>
                                <EditableBadge
                                    prefix="₹"
                                    value={standalonePrice}
                                    min={100000}
                                    max={5000000}
                                    onCommit={(v) => setStandalonePrice(Math.round(Math.min(5000000, Math.max(100000, v))))}
                                    onError={() =>
                                        showFieldError(
                                            setLoanAmountError,
                                            loanAmountErrorTimer,
                                            "Loan amount must be between ₹1,00,000 and ₹50,00,000.",
                                        )
                                    }
                                />
                            </div>
                            <input
                                type="range"
                                className={`${SLIDER_CLASS} ${InterestRateClass}`}
                                min={100000}
                                max={5000000}
                                step={10000}
                                value={standalonePrice}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    setLoanAmountError(null);
                                    setStandalonePrice(Math.round(v));
                                }}
                                style={{ "--pct": `${((standalonePrice - 100000) / (5000000 - 100000)) * 100}%` } as React.CSSProperties}
                            />
                            <div className="text-[#969696] flex justify-between mt-2.5 text-sm font-bold">
                                <span>₹{formatEmiCurrency(100000)}</span>
                                <span>₹{formatEmiCurrency(5000000)}</span>
                            </div>
                            {loanAmountError && (
                                <p className="mt-1 text-[12px] text-red-500">{loanAmountError}</p>
                            )}
                        </div>
                    )}
                    {/* Down Payment — only show when a vehicle variant is selected */}
                    {hasVariants && (
                        <div className="flex flex-col last:pl-1">
                            <div className="flex justify-between items-center mb-2.5">
                                <span className="text-[#374151] text-[13px] font-medium max-[767px]:text-[11px] max-[767px]:font-normal">Down Payment</span>
                                <EditableBadge
                                    prefix="₹"
                                    value={downPayment}
                                    min={0}
                                    max={maxDown}
                                    onCommit={clampDown}
                                    onError={() =>
                                        showFieldError(
                                            setDownPaymentError,
                                            downPaymentErrorTimer,
                                            "Down payment cannot exceed the vehicle price.",
                                        )
                                    }
                                />
                            </div>
                            <input
                                type="range"
                                className={`${SLIDER_CLASS} ${DownPaymentClass}`}
                                min={0}
                                max={Math.ceil(maxDown / 5000)}
                                step={1}
                                value={Math.round(downPayment / 5000)}
                                onChange={(e) => {
                                    const steps = Number(e.target.value);
                                    const maxSteps = Math.ceil(maxDown / 5000);
                                    const value = steps === maxSteps ? maxDown : steps * 5000;
                                    setDownPaymentError(null);
                                    clampDown(value);
                                }}
                                style={{ "--pct": `${downPct}%` } as React.CSSProperties}
                            />
                            <div className="text-[#969696] flex justify-between mt-2.5 text-sm font-bold">
                                <span>₹0</span>
                                <span>₹{formatEmiCurrency(maxDown)}</span>
                            </div>
                            {downPaymentError && (
                                <p className="mt-1 text-[12px] text-red-500">{downPaymentError}</p>
                            )}
                        </div>
                    )}

                    {/* Interest Rate */}
                    <div className="flex flex-col last:pl-1">
                        <div className="flex justify-between items-center mb-2.5">
                            <span className="text-[#374151] text-[13px] font-medium max-[767px]:text-[11px] max-[767px]:font-normal">Interest Rate</span>
                            <EditableBadge
                                suffix="%"
                                value={interestRate}
                                min={MIN_INTEREST}
                                max={MAX_INTEREST}
                                onCommit={clampInterest}
                                onError={() =>
                                    showFieldError(
                                        setInterestRateError,
                                        interestRateErrorTimer,
                                        `“Interest rate must be between ${MIN_INTEREST}% and ${MAX_INTEREST}%.”`,
                                    )
                                }
                            />
                        </div>
                        <input
                            type="range"
                            className={`${SLIDER_CLASS} ${InterestRateClass}`}
                            min={MIN_INTEREST}
                            max={MAX_INTEREST}
                            step={0.1}
                            value={interestRate}
                            onChange={(e) => {
                                setInterestRateError(null);
                                clampInterest(Number(e.target.value));
                            }}
                            style={{ "--pct": `${interestPct}%` } as React.CSSProperties}
                        />
                        <div className="text-[#969696] flex justify-between mt-2.5 text-sm font-bold">
                            <span>{MIN_INTEREST}%</span>
                            <span>{MAX_INTEREST}%</span>
                        </div>
                        {interestRateError && (
                            <p className="mt-1 text-[12px] text-red-500">{interestRateError}</p>
                        )}
                    </div>
                </div>

                {/* ── Loan Period ── */}
                <div className="mt-4 mb-4 max-[767px]:mt-2">
                    <div className="flex justify-between items-center mb-2.5">
                        <span className="text-[#374151] text-[13px] font-medium max-[767px]:text-[11px] max-[767px]:font-normal">Loan Period ( Months )</span>
                        <EditableBadge
                            value={loanMonths}
                            min={12}
                            max={84}
                            onCommit={clampLoanMonths}
                            onError={() =>
                                showFieldError(
                                    setLoanMonthsError,
                                    loanMonthsErrorTimer,
                                    `“Loan duration must be between 12 and 84 months.”`,
                                )
                            }
                            className="!min-w-[50px] !px-3 !py-1 !text-sm"
                        />
                    </div>
                    <div className="flex gap-[7px] max-[639px]:gap-[5px]">
                        {LOAN_PERIODS.map((p) => (
                            <button
                                key={p}
                                className={`text-[#008c8b] cursor-pointer text-center bg-white border-[1.5px] border-[#008c8b] rounded-lg outline-none flex-1 px-1 py-[9px] text-[13px] font-semibold hover:bg-[#edfaf8] ${LoanTermClass} ${p === loanMonths ? "border-[#008c8b] scale-[1.04] !text-white !bg-[#008c8b]" : ""
                                    }`}
                                onClick={() => {
                                    setLoanMonthsError(null);
                                    setLoanMonths(p);
                                }}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    {loanMonthsError && (
                        <p className="mt-1 text-[12px] text-red-500">{loanMonthsError}</p>
                    )}
                </div>

                {/* ── Payable Amount ── */}
                <div className="border-t border-[#f3f4f6] flex justify-between items-center py-3">
                    <span className="text-[#111827] text-base font-semibold">
                        Payable Amount in{" "}
                        <span className="text-[#008c8b]">{loanMonths} </span>
                        months
                    </span>
                    <span className="text-[#111827] text-[17px] font-bold">
                        ₹{formatEmiCurrency(totalPayable)}
                    </span>
                </div>

                {/* ── Loan Breakdown ── */}
                <div className="border-t border-[#f3f4f6] mb-3 pt-3.5 px-0 pb-0.5">

                    {/* Top stat cards */}
                    <div className="grid grid-cols-2 gap-2.5 mb-3.5 max-[767px]:border max-[767px]:border-[#e6edf5] max-[767px]:rounded-xl max-[767px]:grid-cols-1 max-[767px]:gap-0 max-[767px]:overflow-hidden">
                        <div className="rounded-[10px] flex flex-col gap-[3px] px-3.5 py-3 max-[767px]:border-none max-[767px]:border-b max-[767px]:border-[#e6edf5] max-[767px]:rounded-none max-[767px]:flex-row max-[767px]:justify-between max-[767px]:items-center max-[767px]:px-4 max-[767px]:py-3.5 max-[767px]:last:border-b-0 bg-[#edfaf8] border-l-[3px] border-l-[#008c8b] shadow-[0_2px_4px_-3px_#000]">
                            <span className="text-[#6b7280] uppercase tracking-[.04em] text-[11px] font-semibold">Loan Amount</span>
                            <span className="text-[#008c8b] tracking-[-0.4px] text-[18px] font-extrabold leading-[1.2] max-[767px]:text-right max-[767px]:ml-auto max-[767px]:text-base">₹{formatEmiCurrency(principal)}
                                {hasVariants && (
                                    <span className="text-[#9ca3af] ml-[5px] text-[11px] font-medium max-[767px]:hidden block mt-1">{(100 - downPaymentPct).toFixed(1)} % of on-road price</span>
                                )}
                            </span>
                        </div>
                        <div className="rounded-[10px] flex flex-col gap-[3px] px-3.5 py-3 max-[767px]:border-none max-[767px]:border-b max-[767px]:border-[#e6edf5] max-[767px]:rounded-none max-[767px]:flex-row max-[767px]:justify-between max-[767px]:items-center max-[767px]:px-4 max-[767px]:py-3.5 max-[767px]:last:border-b-0 bg-[#fff7ed] border-l-[3px] border-l-[#f97316] shadow-[0_2px_4px_-3px_#000]">
                            <span className="text-[#6b7280] uppercase tracking-[.04em] text-[11px] font-semibold">Total Interest</span>
                            <span className="text-[#008c8b] tracking-[-0.4px] text-[18px] font-extrabold leading-[1.2] max-[767px]:text-right max-[767px]:ml-auto max-[767px]:text-base text-[#f97316]">₹{formatEmiCurrency(totalInterest)}
                                <span className="text-[#9ca3af] ml-[5px] text-[11px] font-medium max-[767px]:hidden block mt-1">@ {interestRate.toFixed(1)}% p.a.</span>
                            </span>
                            <span></span>
                        </div>
                    </div>

                    <div className="mb-3.5">
                        <div className="bg-[#e5e7eb] rounded-full gap-0.5 w-full h-2.5 mb-2 flex overflow-hidden">
                            <div
                                className="bg-[#008c8b] rounded-l-full transition-[width] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                                style={{ width: `${principalPct}%` }}
                            />
                            <div
                                className="bg-[#f97316] rounded-r-full transition-[width] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                                style={{ width: `${100 - principalPct}%` }}
                            />
                        </div>
                        <div className="flex gap-[18px]">
                            <span className="text-[#374151] flex items-center gap-1.5 text-xs font-medium">
                                <span className="bg-[#008c8b] rounded-full shrink-0 w-2.5 h-2.5" />
                                Principal &nbsp;<strong>{principalPct.toFixed(1)}%</strong>
                            </span>
                            <span className="text-[#374151] flex items-center gap-1.5 text-xs font-medium">
                                <span className="bg-[#f97316] rounded-full shrink-0 w-2.5 h-2.5" />
                                Interest &nbsp;<strong>{(100 - principalPct).toFixed(1)}%</strong>
                            </span>
                        </div>
                    </div>

                </div>

                {/* ── EMI Result (desktop only; mobile revamp shows EMI in summary card) ── */}
                {!isMobile && (
                    <div className="bg-[#f3f7ff] border border-[#d2e0ff] rounded-[10px] flex items-center gap-4 px-[18px] py-3.5">
                        <div className="flex flex-col gap-[3px] min-w-[60px]">
                            <span className="text-[#111827] text-[15px] font-bold">EMI</span>
                            <span className="text-[#9ca3af] text-[11px]">Per Month</span>
                        </div>
                        <div className="bg-[#b2e8e2] shrink-0 w-px h-9" />
                        <div className="flex flex-col gap-[3px]">
                            <span className="text-[#008c8b] tracking-[-0.5px] text-[22px] font-extrabold">₹{formatEmiCurrency(emi)}</span>
                            {hasVariants && (
                                <span className="text-[#9ca3af] text-[11px]">Calculated On On-Road Price</span>
                            )}
                        </div>
                    </div>
                )}

                {isEmiRoute && (
                    <div className="border-t border-[#f3f4f6] mt-4 pt-3">
                        <p className="text-[#6b7280] m-0 text-sm leading-[1.45]">

                        </p>
                        <BodyContent
                            headingTag="h2"
                            headingClassName="font-bold mb-3 text-gray-800"
                            fullText="<strong>Disclaimer :</strong> All information, including prices, specifications, and EMI calculations on CarJunction, is provided for general guidance only. Values are estimates and may vary based on location, lender terms, credit approval, and additional charges. CarJunction does not guarantee accuracy and is not responsible for any decisions or losses based on this information; users should verify details with dealers or financial institutions."
                            containerClassName=""
                        />
                    </div>


                )}

            </div>
        </div>
    );

    return (
        <>
            {isModal ? (
                <>
                    {/* Desktop Modal — hidden on mobile */}
                    <div className="hidden sm:flex fixed inset-0 z-[1000] bg-[#0000007a] items-center justify-center" onClick={onClose}>
                        <div
                            className="bg-white rounded-[14px] w-[750px] max-w-[calc(100vw-32px)] overflow-hidden shadow-[0_24px_64px_#00000038] animate-[modalIn_0.22s_cubic-bezier(0.34,1.46,0.64,1)]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={`max-h-[92vh] overflow-y-auto ${SCROLLBAR_CLASS}`}>
                                {content}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Bottom Sheet — hidden on desktop */}
                    <div className="flex sm:hidden fixed inset-0 z-[1000] bg-[#00000073] justify-center items-end" onClick={onClose}>
                        <div
                            className="bg-white rounded-t-[20px] w-full overflow-hidden animate-[sheetIn_0.28s_cubic-bezier(0.32,0.72,0,1)]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={`[scrollbar-gutter:stable] max-h-[94vh] overflow-y-auto ${SCROLLBAR_CLASS}`}>
                                {content}
                            </div>
                        </div>
                    </div>
                </>
            ) : content}
        </>
    );
}
